import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SingleValue } from 'react-select';
import moment from 'moment';
import { Awaited } from '../../../types/typescript';
import createBooking from './createBooking';
import type { ValidationError } from '../request';
import { authService } from '../account/AuthService';
import { DEFAULT_GUEST_COUNT, PaymentMethod, TravelPurpose } from '../constants';
import HotelSearchSelectOption from '../../../types/HotelSearchSelectOption';
import { Coupon } from '../../../types/HotelFromApi';

export type CreateBookingParameters = Parameters<typeof createBooking>[0];
type CreateBookingResponse = Awaited<ReturnType<typeof createBooking>>;

export const NAMESPACE = 'booking';

enum CreateBookingStatus {
  IDLE = 'idle',
  PENDING = 'pending',
  FULFILLED = 'fulfilled',
  REJECTED = 'rejected',
}

export const create = createAsyncThunk<CreateBookingResponse, CreateBookingParameters>(
  `${NAMESPACE}/create`,
  async (body, { rejectWithValue, getState }) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const state = getState() as Record<string, any>;
      const affiliateId =
        process.env.NEXT_PUBLIC_FEATURE_FLAG_PARTNERSHIP_PARAMETER === 'true'
          ? state?.booking?.affiliate?.id
          : undefined;

      const result = await createBooking({ ...body, affiliate_id: affiliateId });

      return result;
    } catch (err) {
      const error = err as ValidationError;

      if (error.response?.data?.detail?.some((d) => d.errorCode === 'price_changed')) {
        return rejectWithValue('TOTAL_PRICE_CHANGED');
      }
      if (error.response?.data?.detail?.some((d) => d.errorCode === 'invalid_discount_id')) {
        return rejectWithValue('INVALID_DISCOUNT_ID');
      }
      if (error.response?.data?.detail?.some((d) => d.errorCode === 'invalid_zenkoku_id')) {
        return rejectWithValue('INVALID_ZENKOKU_ID');
      }
      if (error.response?.data?.detail?.some((d) => d.errorCode === 'unavailable_plan')) {
        return rejectWithValue('UNAVAILABLE_PLAN');
      }
      // TODOd make sure this works
      if (error.response?.data?.detail?.some((d) => d.errorCode === 'invalid_idtoken')) {
        return rejectWithValue('INVALID_IDTOKEN');
      }

      return rejectWithValue('UNKNOWN_ERROR');
    }
  },
  {
    condition: (_, { getState }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { booking } = getState() as Record<string, any>;
      if (
        booking.createBookingStatus === CreateBookingStatus.FULFILLED ||
        booking.createBookingStatus === CreateBookingStatus.PENDING
      ) {
        return false;
      }
      return true;
    },
  }
);

export const initialState = {
  hotelId: '',
  hotelRoomId: -1,
  mealPlanId: '',
  type: '',
  searchTerm: null as SingleValue<HotelSearchSelectOption>,
  checkInDateISOString: '',
  checkOutDateISOString: '',
  roomCount: 1,
  guestCountString: DEFAULT_GUEST_COUNT,
  guestArrivalTime: '',
  travelPurpose: TravelPurpose.WORK,
  roomTypeCode: '',
  paymentMethod: PaymentMethod.PAYPAY as PaymentMethod,
  isPoliciesAccepted: false,
  totalAmount: 0,
  discountedPrice: undefined as number | undefined,
  discountIds: [] as number[],
  promotionIds: [] as string[],
  zenkokuId: 0,
  coupons: [] as Coupon[],
  rooms: [] as {
    adultMaleCount: number;
    adultFemaleCount: number;
    seniorElementarySchoolChildCount: number;
    juniorElementarySchoolChildCount: number;
    infantWithMealCount: number;
    infantWithFutonCount: number;
    infantWithMealAndFutonCount: number;
    infantWithoutMealAndFutonCount: number;
  }[],
  customer: {
    lastNameKanji: '',
    firstNameKanji: '',
    lastNameKana: '',
    firstNameKana: '',
    email: '',
    phone: '',
    country: '',
    postalCode: '',
    region: '',
    address: '',
    specialRequest: '',
    isEmailVerified: false,
  },
  plans: [] as {
    date: string;
    planCode?: string;
  }[],
  affiliate: {
    a8: {} as {
      id: string;
      expiry: number;
    },
    id: undefined as string | undefined,
  },
  bookingResult: {
    bookingId: undefined as string | undefined,
    authToken: undefined as string | undefined,
  },
  createBookingStatus: CreateBookingStatus.IDLE,
  isPriceUpdated: false,
};

const bookingSlice = createSlice({
  name: NAMESPACE,
  initialState,
  reducers: {
    updateSearchCondition(
      state,
      action: PayloadAction<{
        type: string;
        searchTerm: SingleValue<HotelSearchSelectOption>;
        roomCount: number;
        guestCountString: string;
        checkInDateISOString: string;
        checkOutDateISOString: string;
      }>
    ) {
      state.type = action.payload.type;
      state.searchTerm = action.payload.searchTerm;
      state.guestCountString = action.payload.guestCountString;
      state.checkInDateISOString = action.payload.checkInDateISOString;
      state.checkOutDateISOString = action.payload.checkOutDateISOString;
      state.roomCount = action.payload.roomCount;
    },
    updateDateRange(state) {
      state.checkInDateISOString = moment().add(7, 'days').toISOString();
      state.checkOutDateISOString = moment().add(8, 'days').toISOString();
    },
    updateDateAndGuestCount(
      state,
      action: PayloadAction<{
        roomCount: number;
        guestCountString: string;
        checkInDateISOString: string;
        checkOutDateISOString: string;
      }>
    ) {
      state.guestCountString = action.payload.guestCountString;
      state.checkInDateISOString = action.payload.checkInDateISOString;
      state.checkOutDateISOString = action.payload.checkOutDateISOString;
      state.roomCount = action.payload.roomCount;
    },
    updateHotelChoice(
      state,
      action: PayloadAction<{
        hotelId: string;
        hotelRoomId: number;
        mealPlanId: string;
        checkInDateISOString: string;
        checkOutDateISOString: string;
        roomCount: number;
        roomTypeCode: string;
        guestCountString: string;
        totalAmount: number;
        discountedPrice: number;
        discountIds: number[];
        promotionIds: string[];
        plans: {
          date: string;
          planCode?: string;
        }[];
      }>
    ) {
      state.hotelId = action.payload.hotelId;
      state.hotelRoomId = action.payload.hotelRoomId;
      state.mealPlanId = action.payload.mealPlanId;
      state.checkInDateISOString = action.payload.checkInDateISOString;
      state.checkOutDateISOString = action.payload.checkOutDateISOString;
      state.roomCount = action.payload.roomCount;
      state.roomTypeCode = action.payload.roomTypeCode;
      state.guestCountString = action.payload.guestCountString;
      state.totalAmount = action.payload.totalAmount;
      state.discountedPrice = action.payload.discountedPrice;
      state.discountIds = action.payload.discountIds;
      state.promotionIds = action.payload.promotionIds;
      // when user comes back to configure page from customer-detail page, reset zenkokuId and couponIds applied
      state.zenkokuId = 0;
      state.coupons = [];
      state.plans = action.payload.plans;
      state.bookingResult = {
        bookingId: undefined,
        authToken: undefined,
      };
      state.isPriceUpdated = false;
    },
    updateCustomer(
      state,
      action: PayloadAction<{
        lastNameKanji: string;
        firstNameKanji: string;
        lastNameKana: string;
        firstNameKana: string;
        email: string;
        phone: string;
        country: string;
        postalCode: string;
        region: string;
        address: string;
        specialRequest: string;
        isEmailVerified?: boolean;
      }>
    ) {
      state.customer.lastNameKanji = action.payload.lastNameKanji;
      state.customer.firstNameKanji = action.payload.firstNameKanji;
      state.customer.lastNameKana = action.payload.lastNameKana;
      state.customer.firstNameKana = action.payload.firstNameKana;
      state.customer.email = action.payload.email;
      state.customer.phone = action.payload.phone;
      state.customer.country = action.payload.country;
      state.customer.postalCode = action.payload.postalCode;
      state.customer.region = action.payload.region;
      state.customer.address = action.payload.address;
      state.customer.specialRequest = action.payload.specialRequest;
      state.customer.isEmailVerified = action.payload.isEmailVerified ?? false;
    },
    updateRooms(
      state,
      action: PayloadAction<
        {
          adultMaleCount: number;
          adultFemaleCount: number;
          seniorElementarySchoolChildCount: number;
          juniorElementarySchoolChildCount: number;
          infantWithMealCount: number;
          infantWithFutonCount: number;
          infantWithMealAndFutonCount: number;
          infantWithoutMealAndFutonCount: number;
        }[]
      >
    ) {
      state.rooms = action.payload;
    },
    updatePaymentMethod(
      state,
      action: PayloadAction<{
        paymentMethod: PaymentMethod;
      }>
    ) {
      state.paymentMethod = action.payload.paymentMethod;
    },
    updateIsPoliciesAccepted(
      state,
      action: PayloadAction<{
        isPoliciesAccepted: boolean;
      }>
    ) {
      state.isPoliciesAccepted = action.payload.isPoliciesAccepted;
    },
    updateTotalAmount(
      state,
      action: PayloadAction<{
        newTotalAmount: number;
        newDiscountedTotalAmount: number | undefined;
      }>
    ) {
      const { newTotalAmount, newDiscountedTotalAmount } = action.payload;

      // if totalAmount is already set and newTotalAmount is different from the totalAmount,
      // it means price has been updated while user were transitioning pages, set isPriceUpdated flag to true to show an messgae to tell user so
      if (state.totalAmount && newTotalAmount && state.totalAmount !== newTotalAmount) {
        state.isPriceUpdated = true;
      }

      state.totalAmount = newTotalAmount;
      state.discountedPrice = newDiscountedTotalAmount;
    },
    updateGuestArrivalTime(
      state,
      action: PayloadAction<{
        guestArrivalTime: string;
      }>
    ) {
      state.guestArrivalTime = action.payload.guestArrivalTime;
    },
    updateTravelPurpose(
      state,
      action: PayloadAction<{
        travelPurpose: TravelPurpose;
      }>
    ) {
      state.travelPurpose = action.payload.travelPurpose;
    },
    setDiscount(
      state,
      action: PayloadAction<{
        newDiscountIds?: number[];
        newPromotionIds?: string[];
      }>
    ) {
      const { newDiscountIds, newPromotionIds } = action.payload;

      if (newDiscountIds && (newDiscountIds ?? []).length > 0) state.discountIds = newDiscountIds;
      if (newPromotionIds && (newPromotionIds ?? []).length > 0)
        state.promotionIds = newPromotionIds;
    },
    setZenkoku(
      state,
      action: PayloadAction<{
        newDiscountId: number;
      }>
    ) {
      const { newDiscountId } = action.payload;

      state.zenkokuId = newDiscountId;
    },
    setCoupons(
      state,
      action: PayloadAction<{
        newCoupons: Coupon[];
      }>
    ) {
      const { newCoupons } = action.payload;
      state.coupons = newCoupons;
    },
    resetCoupons(state) {
      state.coupons = [];
    },
    setAffiliateParameter(state, action: PayloadAction<{ a8: { id: string; expiry: number } }>) {
      return {
        ...state,
        affiliate: {
          ...state.affiliate,
          a8: action.payload.a8,
        },
      };
    },
    setAffiliateIdParameter(state, action: PayloadAction<string | undefined>) {
      return {
        ...state,
        affiliate: {
          ...state.affiliate,
          id: action.payload,
        },
      };
    },
    resetAffiliateParameter(state) {
      return {
        ...state,
        affiliate: initialState.affiliate,
      };
    },
    resetCustomer(state) {
      return {
        ...state,
        customer: initialState.customer,
      };
    },
    resetDiscount(
      state,
      action: PayloadAction<{
        couponIdToRemove?: number;
        isZenkoku: boolean;
      }>
    ) {
      const { couponIdToRemove, isZenkoku } = action.payload;

      if (isZenkoku) {
        state.zenkokuId = 0;
      } else {
        const newCoupons = state.coupons.filter((coupon) => coupon.id !== couponIdToRemove);
        state.coupons = newCoupons;
      }

      // put it back to the default PayPay in case it sets to pay_at_hotel on payment-selection page
      // when total amount is 0 and user comes back to customer details page to remove the coupon
      state.paymentMethod = PaymentMethod.PAYPAY;
    },
    reset(state) {
      return {
        ...initialState,
        // retain searchbar state
        type: state.type,
        searchTerm: state.searchTerm,
        checkInDateISOString: state.checkInDateISOString,
        checkOutDateISOString: state.checkOutDateISOString,
        roomCount: state.roomCount,
        guestCountString: state.guestCountString,
        affiliate: state.affiliate,
      };
    },
    resetBookingStatus(state) {
      return {
        ...state,
        createBookingStatus: CreateBookingStatus.IDLE,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(create.pending, (state) => {
        state.createBookingStatus = CreateBookingStatus.PENDING;
      })
      .addCase(create.fulfilled, (state, action) => {
        state.createBookingStatus = CreateBookingStatus.FULFILLED;
        state.bookingResult.bookingId = action.payload.bookingId as string;
        state.bookingResult.authToken = action.payload.authToken as string;
        state.affiliate = initialState.affiliate;
        state.rooms = initialState.rooms;
        // Anonymous booking should not save customer information in reducer state unless "create account" is checked
        if (!authService.userPayload) {
          state.customer = initialState.customer;
        }
        authService.resetAffiliateParameter();
      })
      .addCase(create.rejected, (state) => {
        state.createBookingStatus = CreateBookingStatus.REJECTED;
      });
  },
});

export default bookingSlice;
